import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, Award, BookOpen, Users, Calendar, GraduationCap, Heart, Sparkles, ChevronRight } from "lucide-react";
import NewsMarquee from "../components/NewsMarquee";
import { useApply } from "../components/ApplyContext";
import { getNotices, getStats, getCourses, getMessages } from "../lib/api";

const HERO_BG = "/uploads/College-Gate-Hero.jpg";
const CAMPUS_LIFE = "https://static.prod-images.emergentagent.com/jobs/f7c4b906-4127-46a6-93a6-9c0f67cd8c80/images/7f1b6f7604b93a0f09eca5183b19c6f603a0087a4459746574154499422bcfd7.png";
const COLLEGE_BUILDING = "/uploads/College-Building.jpg";

export default function Home() {
  const [notices, setNotices] = useState([]);
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [messages, setMessages] = useState([]);
  const { open: openApply } = useApply();

  useEffect(() => {
    getNotices().then(setNotices).catch(() => {});
    getStats().then(setStats).catch(() => {});
    getCourses("BA").then(setCourses).catch(() => {});
    getMessages().then(setMessages).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative min-h-[88vh] flex items-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(15,76,58,0.92), rgba(15,76,58,0.55), rgba(15,76,58,0.1)), url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="hero"
      >
        <div className="heritage-pattern absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-secondary/20 border border-brand-secondary/40 rounded-full text-brand-secondary text-xs font-medium tracking-[0.2em] uppercase mb-6">
              <Sparkles className="w-3.5 h-3.5" />
              Since 1973 • A Heritage of Empowerment
            </div>
            <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
              Where tradition meets <br />
              <span className="text-brand-secondary italic">ambition.</span>
            </h1>
            <p className="text-white/85 text-base sm:text-lg lg:text-xl max-w-2xl leading-relaxed mb-8">
              Jawad Ali Shah Imambara Girls Postgraduate College — a beacon of
              girls' higher education in Gorakhpur, offering B.A. and M.A.
              programs in a warm, culturally rich environment.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={openApply}
                data-testid="hero-apply-btn"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium bg-brand-secondary text-brand-primary hover:bg-white transition-all"
              >
                Apply for Admission <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/courses"
                data-testid="hero-courses-btn"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium border-2 border-white/40 text-white hover:bg-white hover:text-brand-primary transition-all"
              >
                Explore Courses
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <NewsMarquee notices={notices} />

      {/* STATS BENTO */}
      <section className="py-16 lg:py-24 bg-brand-ivory" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 lg:mb-14 max-w-2xl">
            <div className="eyebrow mb-2">Our Legacy</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-primary leading-tight">
              Five decades of nurturing ambitious minds.
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {[
              { label: "Established", value: stats?.established || "1973", icon: Calendar },
              { label: "Students", value: stats?.students || "1200+", icon: Users },
              { label: "Faculty", value: stats?.faculty || "28", icon: GraduationCap },
              { label: "Alumni", value: stats?.alumni ? `${Math.round(stats.alumni/1000)}K+` : "15K+", icon: Award },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white rounded-3xl p-6 lg:p-8 border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.06)] lift-card"
                data-testid={`stat-${s.label.toLowerCase()}`}
              >
                <div className="w-10 h-10 rounded-full bg-brand-accent flex items-center justify-center text-brand-primary mb-4">
                  <s.icon className="w-5 h-5" />
                </div>
                <div className="font-serif text-4xl lg:text-5xl text-brand-primary font-semibold mb-1">
                  {s.value}
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-brand-muted">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COURSES HIGHLIGHT */}
      <section className="py-16 lg:py-24 bg-brand-accent/50" data-testid="courses-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div className="max-w-2xl">
              <div className="eyebrow mb-2">Academic Programs</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-primary leading-tight">
                Explore our arts & humanities curriculum.
              </h2>
            </div>
            <Link
              to="/courses"
              data-testid="view-all-courses"
              className="inline-flex items-center gap-2 text-brand-primary font-medium hover:text-brand-tertiary transition-colors"
            >
              View all courses <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Horizontal scroll on mobile, grid on desktop */}
          <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-4 lg:grid lg:grid-cols-3 lg:gap-6 lg:overflow-visible">
            {courses.slice(0, 6).map((c, i) => {
              const Icon = Icons[c.icon] || BookOpen;
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="snap-start shrink-0 w-[82%] sm:w-[60%] lg:w-auto"
                  data-testid={`course-card-${c.subject.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Link
                    to="/courses"
                    className="block bg-white rounded-3xl overflow-hidden border border-brand-primary/10 lift-card h-full group relative"
                  >
                    {/* Decorative blob */}
                    <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-brand-accent transition-all duration-500 group-hover:scale-125" aria-hidden="true" />
                    <div className="relative p-6 lg:p-7 flex flex-col h-full min-h-[260px]">
                      <div className="flex items-start justify-between mb-5">
                        <div className="w-14 h-14 rounded-2xl bg-brand-primary text-brand-secondary flex items-center justify-center">
                          <Icon className="w-6 h-6" strokeWidth={1.6} />
                        </div>
                        <span className="px-2.5 py-1 bg-brand-secondary text-brand-primary text-[10px] font-semibold tracking-[0.25em] uppercase rounded-full">
                          {c.level}
                        </span>
                      </div>
                      <h3 className="font-serif text-2xl lg:text-3xl text-brand-primary leading-tight mb-2">
                        {c.subject}
                      </h3>
                      <p className="text-sm text-brand-ink/75 leading-relaxed mb-5 line-clamp-3 flex-1">
                        {c.description}
                      </p>
                      <div className="flex items-center gap-2 text-brand-secondary font-medium text-sm pt-4 border-t border-brand-primary/10">
                        Learn more <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MESSAGE FROM LEADERSHIP */}
      {messages && messages.length > 0 && (
        <section className="py-16 lg:py-24" data-testid="leadership-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 lg:mb-14 max-w-2xl">
              <div className="eyebrow mb-2">Voices of our institution</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-primary leading-tight">
                A message from our leadership.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
              {messages.slice(0, 2).map((m, i) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="bg-white rounded-3xl overflow-hidden border border-brand-primary/10 shadow-[0_8px_30px_rgba(15,76,58,0.06)] grid grid-cols-3"
                  data-testid={`leader-msg-${i}`}
                >
                  <div className="col-span-1 relative bg-brand-primary">
                    {m.photo ? (
                      <img
                        src={m.photo}
                        alt={m.from_name}
                        className="absolute inset-0 w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-brand-secondary/60">
                        <GraduationCap className="w-16 h-16" />
                      </div>
                    )}
                    {/* gold corner */}
                    <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 border-brand-secondary" />
                    <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 border-brand-secondary" />
                  </div>
                  <div className="col-span-2 p-5 lg:p-7">
                    <div className="font-serif text-xl lg:text-2xl text-brand-primary font-semibold leading-tight">
                      {m.from_name}
                    </div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-secondary font-semibold mt-1 mb-3">
                      {m.from_role}
                    </div>
                    <p className="text-brand-ink/80 leading-relaxed text-sm font-serif italic">
                      &ldquo;{m.message.length > 220 ? m.message.slice(0, 220) + "..." : m.message}&rdquo;
                    </p>
                    <Link
                      to="/about"
                      data-testid={`leader-readmore-${i}`}
                      className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-secondary mt-4 transition-colors"
                    >
                      Read full message <ChevronRight className="w-3 h-3" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VALUES BAND */}
      <section className="py-16 lg:py-24 bg-brand-primary text-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 lg:mb-14 max-w-2xl">
            <div className="text-brand-secondary text-xs tracking-[0.3em] uppercase mb-2">
              Our Core Values
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Rooted in heritage. Built for tomorrow.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: Heart,
                title: "Empowerment",
                text: "Preparing every young woman to lead with confidence and compassion.",
              },
              {
                icon: BookOpen,
                title: "Excellence",
                text: "Rigorous academic programs grounded in arts and humanities.",
              },
              {
                icon: Users,
                title: "Inclusivity",
                text: "A welcoming home for students of every background and community.",
              },
            ].map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-6 lg:p-8 border border-white/15 rounded-3xl bg-white/5 backdrop-blur-sm"
                data-testid={`value-${v.title.toLowerCase()}`}
              >
                <div className="w-12 h-12 rounded-full bg-brand-secondary text-brand-primary flex items-center justify-center mb-4">
                  <v.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif text-2xl mb-2 text-brand-secondary">
                  {v.title}
                </h3>
                <p className="text-white/80 leading-relaxed text-sm">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CALL TO ACTION */}
      <section className="py-16 lg:py-24 bg-brand-ivory" data-testid="cta-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden rounded-3xl p-8 sm:p-12 lg:p-16 border border-brand-primary/10"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(15,76,58,0.92), rgba(15,76,58,0.6)), url(${COLLEGE_BUILDING})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="max-w-2xl text-white">
              <div className="text-brand-secondary text-xs tracking-[0.3em] uppercase mb-2">
                Admissions 2026-27 now open
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mb-4">
                Begin your journey with us.
              </h2>
              <p className="text-white/85 mb-8 leading-relaxed">
                Applications for B.A. and M.A. programs are now open. Secure
                your seat and join a thriving community of learners.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={openApply}
                  data-testid="cta-apply-btn"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium bg-brand-secondary text-brand-primary hover:bg-white transition-all"
                >
                  Apply Now <ArrowRight className="w-4 h-4" />
                </button>
                <Link
                  to="/contact"
                  data-testid="cta-contact-btn"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium border-2 border-white/40 text-white hover:bg-white hover:text-brand-primary transition-all"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
